import React, { useState, useEffect } from 'react';
import FeatherIcon from "feather-icons-react";
import { Logo } from "../_components/Imagepath";
import { useAuth } from '../context/AuthContext';   
import api from '../api/api';
import { useTranslation } from 'react-i18next';
import Lang from '../common/Lang';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const Feedback = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');

    // Check if user is authenticated and auto-fill data
    useEffect(() => {
        // Get user data from localStorage or context
        if (user) {
            try {
                setFormData(prev => ({
                    ...prev,
                    name: user.name || 'not',
                    email: user.email || ''
                }));
            } catch (error) {
                console.log('No user data found or invalid format');
            }
        }
    }, [user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);

        try {
            const response = await api.post('/feedback/', formData);
            
            setMessage(response.data.message || 'Thank you for your feedback! We will get back to you soon.');
            setMessageType('success');
            
            // Reset form
            setFormData({
                name: formData.name, // Keep name if user is authenticated
                email: formData.email, // Keep email if user is authenticated
                subject: '',
                message: ''
            });
        } catch (error) {
            console.error('Feedback submission error:', error);
            
            let errorMessage = 'Failed to send feedback. Please try again.';
            
            // Handle specific error messages from the API
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data) {
                // Handle validation errors
                const errors = [];
                Object.entries(error.response.data).forEach(([, values]) => {
                    if (Array.isArray(values)) {
                        errors.push(...values);
                    } else {
                        errors.push(values);
                    }
                });
                if (errors.length > 0) {
                    errorMessage = errors.join(', ');
                }
            }
            
            setMessage(errorMessage);
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="feedback-page" style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
            <div className="container-fluid">
                <div className="row justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                    <div className="col-12 col-lg-8 col-xl-6">
                        <div className="row">
                            <div className="col-12">
                                <div className="card bg-white">
                                    <div className="card-header">
                                        <div className="d-flex justify-content-end mb-2 align-items-center">
                                            <Lang />
                                        </div>
                                        <div className="d-flex flex-column gap-2 align-items-center justify-content-center">
                                            <img src={Logo} alt="Kflow Logo" height="32" className="me-2" />
                                            <h4 className="card-title mb-0">{t("Send us your feedback")}</h4>
                                        </div>
                                        <p className="text-muted text-center mt-2 mb-0">
                                            {t("We'd love to hear from you. Send us a message and we'll respond as soon as possible.")}
                                        </p>
                                    </div>
                                    
                                    <div className="card-body">
                                        {message && (
                                            <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-danger'} d-flex align-items-center`}>
                                                <FeatherIcon 
                                                    icon={messageType === 'success' ? 'check-circle' : 'alert-circle'} 
                                                    size={20} 
                                                    className="me-2" 
                                                />
                                                {message}
                                            </div>
                                        )}
                                        
                                        <form onSubmit={handleSubmit}>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label htmlFor="name" className="form-label">
                                                            <FeatherIcon icon="user" size={16} className="me-1" />
                                                            {t('Name')} *
                                                        </label>
                                                        <input
                                                            type="text"
                                                            id="name"
                                                            name="name"
                                                            className="form-control"
                                                            value={formData.name}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder={t("Enter your name")}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-group mb-3">
                                                        <label htmlFor="email" className="form-label">
                                                            <FeatherIcon icon="mail" size={16} className="me-1" />
                                                            Email *
                                                        </label>
                                                        <input
                                                            type="email"
                                                            id="email"
                                                            name="email"
                                                            className="form-control"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            required
                                                            placeholder={t("Enter your email address")}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="form-group mb-3">
                                                <label htmlFor="subject" className="form-label">
                                                    <FeatherIcon icon="bookmark" size={16} className="me-1" />
                                                    {t("Subject")} *
                                                </label>
                                                <input
                                                    type="text"
                                                    id="subject"
                                                    name="subject"
                                                    className="form-control"
                                                    value={formData.subject}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder={t("Enter the subject")}
                                                />
                                            </div>
                                            
                                            <div className="form-group mb-4">
                                                <label htmlFor="message" className="form-label">
                                                    <FeatherIcon icon="message-square" size={16} className="me-1" />
                                                    {t("Message")} *
                                                </label>
                                                <textarea
                                                    id="message"
                                                    name="message"
                                                    className="form-control"
                                                    rows="6"
                                                    value={formData.message}
                                                    onChange={handleInputChange}
                                                    required
                                                    placeholder={t("Enter your message...")}
                                                ></textarea>
                                            </div>
                                            
                                            <div className="d-flex justify-content-start">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary btn-lg"
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t("Sending...")}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FeatherIcon icon="send" size={16} className="me-2" />
                                                            {t("Send Feedback")}
                                                        </>
                                                    )}
                                                </button>
                                                <Link to="/dashboard" className="btn btn-secondary btn-lg ms-3">
                                                    <FeatherIcon icon="arrow-left" size={16} className="me-2" />
                                                    {t("Back to Dashboard")}
                                                </Link>
                                            </div>
                                        </form>
                                    </div>
                                    
                                    <div className="card-footer text-center">
                                        <div className="d-flex align-items-center justify-content-center text-muted">
                                            <FeatherIcon icon="shield" size={16} className="me-1" />
                                            <small>{t("Your information is secure and will not be shared.")}</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Feedback;
